import { Button, Card, Divider, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { MissingDailyRevenueParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { OrganizationFilter } from './OrganizationFilter';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

type MissingDailyRevenueDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const initialDateRange: MissingDailyRevenueDateRangeValue = {
	startDate: null,
	endDate: null,
};

const buildMissingDailyRevenueParameters = ({
	startDate,
	endDate,
	includePending,
	organizationId,
}: {
	startDate: Dayjs;
	endDate: Dayjs;
	includePending: boolean;
	organizationId: number | null;
}): MissingDailyRevenueParameters => {
	const parameters: MissingDailyRevenueParameters = {
		startDate: startDate.format('YYYY-MM-DD'),
		endDate: endDate.format('YYYY-MM-DD'),
		includePending,
	};

	if (organizationId) {
		parameters.organizationId = organizationId;
	}

	return parameters;
};

export const MissingDailyRevenueReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<MissingDailyRevenueDateRangeValue>(initialDateRange);
	const [includePending, setIncludePending] = useState(false);
	const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(organizationId ?? null);

	const reset = () => {
		setDateRange(initialDateRange);
		setIncludePending(false);
		setSelectedOrganizationId(organizationId ?? null);
	};

	const validate = () => {
		if (!selectedOrganizationId) {
			onValidationError('Select an organization.');
			return false;
		}

		if (!dateRange.startDate) {
			onValidationError('Select a start date.');
			return false;
		}

		if (!dateRange.endDate) {
			onValidationError('Select an end date.');
			return false;
		}

		if (dateRange.endDate.isBefore(dateRange.startDate, 'day')) {
			onValidationError('End date must be on or after the start date.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate() || !dateRange.startDate || !dateRange.endDate) {
			return;
		}

		await createReport({
			reportType: 'missingDailyRevenue',
			parameters: buildMissingDailyRevenueParameters({
				startDate: dateRange.startDate,
				endDate: dateRange.endDate,
				includePending,
				organizationId: selectedOrganizationId,
			}),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<OrganizationFilter value={selectedOrganizationId} onChange={setSelectedOrganizationId} />

				<Divider />

				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							DR dates
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker<Dayjs>
								label='Start date'
								value={dateRange.startDate}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker<Dayjs>
								label='End date'
								value={dateRange.endDate}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<FormControlLabel
					control={
						<Switch
							checked={includePending}
							onChange={(event) => setIncludePending(event.target.checked)}
							inputProps={{ 'aria-label': 'Include pending daily reports' }}
						/>
					}
					label='Include Pending Daily Reports'
				/>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button
						onClick={submit}
						disabled={
							!selectedOrganizationId || !dateRange.startDate || !dateRange.endDate || isGeneratingReport
						}
					>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
